import React, { useState, useEffect } from "react";
import {
  ref,
  listAll,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";
import { storage, db } from "../../../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  Table, 
  Card, 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  InputGroup, 
  FormControl, 
  Badge, 
  Spinner,
  ProgressBar,
  Tabs,
  Tab,
  Modal,
  Dropdown,
  Image
} from "react-bootstrap";
import { 
  FaSearch, 
  FaUpload, 
  FaImage, 
  FaEdit, 
  FaTrash, 
  FaDownload, 
  FaPlus,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTimes
} from "react-icons/fa";

const AdminBanners = () => {
  const services = [
    "Used Car warranty",
    "Vehicle inspection",
    "Vehicle Service",
    "Road Side",
    "Battery Replacement",
    "Windscreen Repair",
    "Home",
  ];

  const [selectedService, setSelectedService] = useState("Used Car warranty");
  const [images, setImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [imageList, setImageList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [heading, setHeading] = useState("");
  const [paragraph, setParagraph] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [activeTab, setActiveTab] = useState("upload");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchImages();
  }, [selectedService]);

  const fetchImages = async () => {
    setLoading(true);
    setError("");
    try {
      const allImages = [];
      for (const service of services) {
        const folderRef = ref(storage, `${service}/`);
        const result = await listAll(folderRef);
        const urls = await Promise.all(
          result.items.map(async (itemRef) => {
            const url = await getDownloadURL(itemRef);
            const q = query(
              collection(db, "images"),
              where("name", "==", itemRef.name),
              where("service", "==", service)
            );
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              let docData = querySnapshot.docs[0].data();
              return {
                id: querySnapshot.docs[0].id,
                name: itemRef.name,
                url,
                service,
                heading: docData.heading || "",
                paragraph: docData.paragraph || "",
              };
            } else {
              // Create a new document for the image
              const docRef = await addDoc(collection(db, "images"), {
                name: itemRef.name,
                service: service,
                url: url,
                heading: "",
                paragraph: "",
              });
              return {
                id: docRef.id,
                name: itemRef.name,
                url,
                service,
                heading: "",
                paragraph: "",
              };
            }
          })
        );
        allImages.push(...urls);
      }
      setImageList(allImages);
    } catch (error) {
      console.error("Error fetching images:", error);
      setError("Failed to fetch images");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
  };

  const handleImageChange = (e) => {
    if (e.target.files) {
      setImages(Array.from(e.target.files));
    }
  };

  const handleUpload = () => {
    if (!images.length) {
      toast.error("Please select at least one image to upload");
      return;
    }

    images.forEach((image) => {
      const storageRef = ref(storage, `${selectedService}/${image.name}`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({
            ...prev,
            [image.name]: progress,
          }));
        },
        (error) => {
          console.error("Upload error:", error);
          setError("Failed to upload image");
          toast.error(`Upload failed: ${error.message}`);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            await addDoc(collection(db, "images"), {
              url: downloadURL,
              name: image.name,
              service: selectedService,
              heading: heading,
              paragraph: paragraph,
            });
            fetchImages(); // Refresh the image list
            toast.success("Image uploaded successfully");
          } catch (error) {
            console.error("Error adding document: ", error);
            setError("Failed to save image metadata");
            toast.error("Failed to save image details");
          }
        }
      );
    });
  };

  const handleDelete = async (name, service) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      const storageRef = ref(storage, `${service}/${name}`);
      try {
        await deleteObject(storageRef);
        const q = query(
          collection(db, "images"),
          where("service", "==", service),
          where("name", "==", name)
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(async (doc) => {
          await deleteDoc(doc.ref);
        });
        fetchImages(); // Refresh the image list
        toast.success("Image deleted successfully");
      } catch (error) {
        console.error("Error deleting image:", error);
        setError("Failed to delete image");
        toast.error("Failed to delete image");
      }
    }
  };

  const handleDownload = (url, name) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    toast.info(`Downloading ${name}`);
  };

  const handleEdit = (image) => {
    setSelectedImage(image);
    setHeading(image.heading);
    setParagraph(image.paragraph);
    setPreviewUrl(image.url);
    setShowModal(true);
  };

  const handleImagePreviewChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (selectedImage) {
        const docRef = doc(db, "images", selectedImage.id);

        if (newImage) {
          // If replacing image, delete the old one first
          const oldStorageRef = ref(storage, `${selectedImage.service}/${selectedImage.name}`);
          try {
            await deleteObject(oldStorageRef);
          } catch (error) {
            console.error("Error deleting old image:", error);
          }

          const storageRef = ref(storage, `${selectedImage.service}/${newImage.name}`);
          const uploadTask = uploadBytesResumable(storageRef, newImage);

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload is ${progress}% done`);
            },
            (error) => {
              console.error("Upload error:", error);
              setError("Failed to upload image");
              toast.error("Failed to upload new image");
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              await updateDoc(docRef, {
                heading: heading,
                paragraph: paragraph,
                url: downloadURL,
                name: newImage.name,
              });
              setShowModal(false);
              fetchImages(); // Refresh the image list
              toast.success("Banner updated successfully");
            }
          );
        } else {
          await updateDoc(docRef, {
            heading: heading,
            paragraph: paragraph,
          });
          setShowModal(false);
          fetchImages(); // Refresh the image list
          toast.success("Banner details updated successfully");
        }
      }
    } catch (error) {
      console.error("Error updating document: ", error);
      setError("Failed to update image metadata");
      toast.error("Failed to update banner details");
    }
  };

  const resetForm = () => {
    setHeading("");
    setParagraph("");
    setImages([]);
    setUploadProgress({});
  };

  // Filter images based on search term and selected service
  const filteredImages = imageList.filter(
    (image) => 
      (selectedService === "All" || image.service === selectedService) &&
      (searchTerm === "" || 
       (image.heading && image.heading.toLowerCase().includes(searchTerm.toLowerCase())) ||
       (image.paragraph && image.paragraph.toLowerCase().includes(searchTerm.toLowerCase())) ||
       (image.name && image.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
       (image.service && image.service.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  // Function to remove image from selection before upload
  const removeImageFromSelection = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
    
    // Also remove from progress tracking if exists
    if (images[index] && images[index].name) {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[images[index].name];
        return newProgress;
      });
    }
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-primary">
                    <FaImage className="me-2" /> Banner Management
                  </h5>
                  {loading ? (
                    <Spinner animation="border" size="sm" className="ms-3" />
                  ) : (
                    <Badge bg="info" className="ms-2">
                      {filteredImages.length}
                    </Badge>
                  )}
                </div>
                
                <div className="d-flex">
                  {activeTab === "manage" && (
                    <InputGroup className="me-3" style={{ width: "250px" }}>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <FormControl
                        placeholder="Search banners..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  )}
                  
                  <Button 
                    variant="outline-primary" 
                    className="d-flex align-items-center"
                    onClick={fetchImages}
                  >
                    <FaSyncAlt className="me-2" /> Refresh
                  </Button>
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="border-0 px-3 pt-3"
                >
                  <Tab eventKey="upload" title={<span><FaUpload className="me-2" />Upload Banners</span>}>
                    <div className="p-4">
                      <Row>
                        <Col md={6} className="mb-4">
                          <Form.Group className="mb-3">
                            <Form.Label>Select Service</Form.Label>
                            <Form.Select
                              value={selectedService}
                              onChange={handleServiceChange}
                            >
                              {services.map((service) => (
                                <option key={service} value={service}>
                                  {service}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Banner Heading</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter a heading for the banner"
                              value={heading}
                              onChange={(e) => setHeading(e.target.value)}
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Banner Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              placeholder="Enter a description for the banner"
                              value={paragraph}
                              onChange={(e) => setParagraph(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Upload Banner Images</Form.Label>
                            <div className="p-4 border rounded bg-light text-center mb-3">
                              <FaImage size={32} className="text-muted mb-3" />
                              <p className="mb-2">Drag and drop images here or click to browse</p>
                              <Form.Control
                                type="file"
                                multiple
                                onChange={handleImageChange}
                                className="d-none"
                                id="bannerImageUpload"
                              />
                              <Button
                                variant="outline-primary"
                                onClick={() => document.getElementById('bannerImageUpload').click()}
                              >
                                <FaUpload className="me-2" /> Select Images
                              </Button>
                              <div className="text-muted mt-2"><small>Supported formats: JPG, PNG, GIF</small></div>
                            </div>
                          </Form.Group>

                          {images.length > 0 && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Selected Images ({images.length})</h6>
                                <Button 
                                  variant="link" 
                                  className="p-0 text-muted" 
                                  onClick={resetForm}
                                >
                                  Clear All
                                </Button>
                              </div>
                              <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {images.map((image, idx) => (
                                  <div key={idx} className="d-flex justify-content-between align-items-center py-2 px-3 mb-2 bg-white rounded shadow-sm">
                                    <div className="d-flex align-items-center">
                                      <div className="bg-light rounded p-1 me-2">
                                        <FaImage className="text-muted" />
                                      </div>
                                      <span className="text-truncate" style={{ maxWidth: '180px' }}>
                                        {image.name}
                                      </span>
                                    </div>
                                    <Button 
                                      variant="link" 
                                      className="p-0 text-danger" 
                                      onClick={() => removeImageFromSelection(idx)}
                                    >
                                      <FaTimes />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {Object.keys(uploadProgress).length > 0 && (
                            <div className="mb-3">
                              <h6 className="mb-3">Upload Progress</h6>
                              {Object.entries(uploadProgress).map(([name, progress]) => (
                                <div key={name} className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <small className="text-truncate" style={{ maxWidth: '80%' }}>{name}</small>
                                    <small>{Math.round(progress)}%</small>
                                  </div>
                                  <ProgressBar 
                                    now={progress} 
                                    variant={progress === 100 ? "success" : "primary"} 
                                    animated={progress < 100} 
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </Col>
                      </Row>
                      
                      <div className="d-flex justify-content-end mt-3">
                        <Button variant="outline-secondary" className="me-2" onClick={resetForm}>
                          Reset
                        </Button>
                        <Button variant="primary" onClick={handleUpload} disabled={images.length === 0}>
                          <FaUpload className="me-2" /> Upload Banners
                        </Button>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab eventKey="manage" title={<span><FaImage className="me-2" />Manage Banners</span>}>
                    <div className="p-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <Form.Group className="d-flex align-items-center mb-0">
                          <Form.Label className="me-2 mb-0">Filter by Service:</Form.Label>
                          <Form.Select 
                            value={selectedService} 
                            onChange={handleServiceChange}
                            style={{ width: '200px' }}
                          >
                            <option value="All">All Services</option>
                            {services.map(service => (
                              <option key={service} value={service}>{service}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                        
                        <Button 
                          variant="success" 
                          size="sm" 
                          className="d-flex align-items-center"
                          onClick={() => {
                            setActiveTab("upload");
                            resetForm();
                          }}
                        >
                          <FaPlus className="me-1" /> Add New Banner
                        </Button>
                      </div>
                      
                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-3 text-muted">Loading banners...</p>
                        </div>
                      ) : error ? (
                        <div className="alert alert-danger">
                          <FaExclamationTriangle className="me-2" />
                          {error}
                        </div>
                      ) : filteredImages.length === 0 ? (
                        <div className="text-center py-5 empty-state">
                          <FaExclamationTriangle size={48} className="text-muted mb-3" />
                          <h5>No banners found</h5>
                          <p className="text-muted">
                            {searchTerm ? 
                              "No banners match your search criteria." : 
                              `There are no banners ${selectedService !== "All" ? `for ${selectedService}` : ""} at this time.`
                            }
                          </p>
                          {searchTerm && (
                            <Button variant="outline-primary" onClick={() => setSearchTerm("")}>
                              Clear Search
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Row>
                          {filteredImages.map((image, index) => (
                            <Col key={index} lg={4} md={6} className="mb-4">
                              <Card className="h-100 shadow-sm">
                                <div style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
                                  <Image 
                                    src={image.url} 
                                    alt={image.name}
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover' 
                                    }}
                                  />
                                  <div className="position-absolute top-0 start-0 p-2">
                                    <Badge bg="primary" pill className="text-white">
                                      {image.service}
                                    </Badge>
                                  </div>
                                </div>
                                <Card.Body>
                                  <Card.Title className="text-truncate">
                                    {image.heading || "(No Heading)"}
                                  </Card.Title>
                                  <Card.Text className="text-muted" style={{ height: '48px', overflow: 'hidden' }}>
                                    {image.paragraph || "(No Description)"}
                                  </Card.Text>
                                  <small className="d-block text-truncate text-muted mb-3">
                                    {image.name}
                                  </small>
                                  <div className="d-flex justify-content-between">
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm"
                                      onClick={() => handleEdit(image)}
                                    >
                                      <FaEdit className="me-1" /> Edit
                                    </Button>
                                    <div>
                                      <Button 
                                        variant="outline-info" 
                                        size="sm" 
                                        className="me-2"
                                        onClick={() => handleDownload(image.url, image.name)}
                                      >
                                        <FaDownload />
                                      </Button>
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => handleDelete(image.name, image.service)}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Container>
        </main>
      </div>
      
      {/* Edit Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Banner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Banner Heading</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter a heading for the banner"
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Banner Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter a description for the banner"
                  value={paragraph}
                  onChange={(e) => setParagraph(e.target.value)}
                />
              </Form.Group>
              
              <Form.Group>
                <Form.Label>Replace Image (Optional)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleImagePreviewChange}
                />
                <Form.Text className="text-muted">
                  Leave this blank if you don't want to change the image.
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <div className="mb-3">
                <Form.Label>Preview</Form.Label>
                <div 
                  className="border rounded d-flex align-items-center justify-content-center bg-light"
                  style={{ height: '250px' }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
                    />
                  ) : (
                    <div className="text-center text-muted">
                      <FaImage size={48} className="mb-2" />
                      <p>No image preview available</p>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedImage && (
                <div>
                  <Form.Label>Current Details</Form.Label>
                  <Table size="sm" bordered>
                    <tbody>
                      <tr>
                        <th style={{ width: '120px' }}>Service</th>
                        <td>{selectedImage.service}</td>
                      </tr>
                      <tr>
                        <th>File Name</th>
                        <td className="text-truncate">{selectedImage.name}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminBanners;